import Component from '@cx-modules/bet-builder';
import BetBuilderCss from '@cx-modules/bet-builder/style.css?inline';
import { widgetBootstrap } from '../../services/cx-module-helper';
import loader from './assets/loader';
import themes from './themes';
import themeContract from './themes/contract';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const {
  VITE_CLICKOUT_SERVICE_URL: clickoutServiceUrl,
  VITE_OC_API_HOST: ocApiHost
} = import.meta.env;

const getVariation = (
  bookies: Record<string, object> | undefined,
  betIds: number[] | undefined,
  automatedPrePopulatedBets: unknown
) => {
  const numberOfBookies = Object.keys(bookies || {}).length;
  const betIdsLength = betIds?.length || 0;
  if (automatedPrePopulatedBets) {
    if (numberOfBookies > 1) return 'automated_pre_populated';
    return 'automated_pre_populated_single_operator';
  }

  if (numberOfBookies > 1) {
    return betIdsLength > 0 ? 'pre_populated' : 'build_your_own';
  }

  return betIdsLength > 0
    ? 'pre_populated_single_operator'
    : 'build_your_own_single_operator';
};

type ContractParams = {
  affIds?: unknown;
  apiKey?: unknown;
  automatedPrePopulatedBets?: unknown;
  analytics?: { getDataLayer: () => unknown } | false;
  betIds?: number[];
  bookies?: Record<string, object>;
  campaignName?: unknown;
  categoryGroupId?: number | string;
  compactMode?: unknown;
  eventId?: number | string;
  geolocation?: string;
  oddsFormat?: unknown;
  marketType?: unknown;
  repub?: string;
  subeventId?: number | string;
  widgetStyle?: string;
  hash?: unknown;
};

type ContractProps = {
  affIds?: unknown;
  analyticsImpressionEventData: unknown;
  apiKey?: unknown;
  automatedPrePopulatedBets?: unknown;
  clickoutServiceUrl?: string;
  dataApiEndpoint?: string;
  compactMode?: unknown;
  disableAnalytics: boolean;
  oddsFormat?: unknown;
  repub?: string;
  analyticsImpressionEventName: string;
  geolocation?: string;
  filterBookies: string[];
  analyticsDataLayer?: unknown;
  categoryGroupId?: number;
  eventId?: number;
  initialSubeventId?: number;
  theme?: string;
  initialBetIds?: number[];
  disclaimerHtml?: string;
};

export const contract = async ({
  affIds,
  apiKey,
  automatedPrePopulatedBets,
  analytics,
  betIds,
  bookies,
  campaignName,
  categoryGroupId,
  compactMode,
  eventId,
  geolocation,
  oddsFormat,
  marketType,
  repub,
  subeventId,
  widgetStyle,
  hash: widgetSlot
}: ContractParams) => {
  const analyticsImpressionEventData = {
    campaignName,
    eventType: marketType,
    widgetType: 'bet-builder',
    variation: getVariation(bookies, betIds, automatedPrePopulatedBets),
    widgetSlot
  };

  const props: ContractProps = {
    affIds,
    analyticsImpressionEventData,
    apiKey,
    automatedPrePopulatedBets,
    clickoutServiceUrl,
    dataApiEndpoint: ocApiHost,
    compactMode,
    disableAnalytics: !analytics,
    oddsFormat,
    repub,
    analyticsImpressionEventName: 'bet_builder_impressions',
    geolocation,
    filterBookies: Object.keys(bookies || {})
  };

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  if (categoryGroupId) {
    props.categoryGroupId = Number.parseInt(String(categoryGroupId), 10);
  }

  if (eventId) {
    props.eventId = Number.parseInt(String(eventId), 10);
  }

  if (subeventId) {
    props.initialSubeventId = Number.parseInt(String(subeventId), 10);
  }

  if (widgetStyle) {
    props.theme = widgetStyle;
  }

  if (betIds) {
    props.initialBetIds = betIds;
  }
  if (
    repub === 'OC' &&
    widgetStyle !== 'oddschecker' &&
    widgetStyle !== 'whoscored'
  ) {
    props.disclaimerHtml = `<span>18+ T&Cs Apply. <a href='https://gambleaware.org' target='_blank'>gambleaware.org</a> | Play Responsibly</span>`;
  }

  return props;
};

type QueryKeyParams = {
  betIds?: number[];
  bookies?: Record<string, object>;
  categoryGroupId?: number | string;
  eventId?: number;
  geolocation?: string;
  repub?: string;
  subeventId?: number | string;
};

export const queryKey = ({
  betIds,
  bookies,
  categoryGroupId,
  eventId,
  geolocation,
  repub,
  subeventId
}: QueryKeyParams) => {
  const filteredBookieCodes = Object.keys(bookies || {});
  const [country, subdivision] = (geolocation || '').split('-');
  const countryCode = country ? country.toUpperCase() : 'GB';
  const subdivisionCode = subdivision ? subdivision.toUpperCase() : '';

  const keys: unknown[][] = [];

  if (categoryGroupId) {
    keys.push([
      'bet-builder',
      Number.parseInt(String(categoryGroupId), 10),
      filteredBookieCodes,
      repub
    ]);
  } else if (eventId) {
    // Rolling campaigns have an eventId but no categoryGroupId.
    // @cx-modules/bet-builder falls back to categoryGroupId 2 (socer), so the events key
    // must match that same fallback or the pre-hydrated cache will never hit.
    keys.push(['bet-builder', 2, filteredBookieCodes, repub]);
  }

  if (subeventId) {
    keys.push([
      'bet-builder',
      'subevent-markets',
      Number.parseInt(String(subeventId), 10),
      [...filteredBookieCodes].sort(),
      repub,
      countryCode,
      subdivisionCode
    ]);
  }

  if (betIds?.length) {
    const sortedBetIds = [...betIds].sort((a, b) => a - b);
    const sortedBookieCodes = [...filteredBookieCodes].sort();

    keys.push([
      'bet-builder',
      'prices',
      sortedBetIds,
      sortedBookieCodes,
      repub,
      countryCode,
      subdivisionCode
    ]);

    keys.push([
      'bet-builder',
      'bets',
      sortedBetIds,
      sortedBookieCodes,
      repub,
      countryCode,
      subdivisionCode
    ]);
  }

  return keys;
};

const KNOWN_KEY_TYPES = new Set(['subevent-markets', 'prices', 'bets']);

const classifyQueryKey = (key: unknown[]) =>
  KNOWN_KEY_TYPES.has(key[1] as string) ? key[1] : 'events';

const classifyData = (data: unknown) => {
  if (!data || typeof data !== 'object') return undefined;
  if ('events' in data) return 'events';
  if ('markets' in data) return 'subevent-markets';
  if ('bets' in data) return 'bets';
  if ('data' in data) return 'prices';
  return undefined;
};

export const alignDehydratedData = (queryKeys: unknown, data: unknown) => {
  if (
    !Array.isArray(queryKeys) ||
    !Array.isArray(queryKeys[0]) ||
    !Array.isArray(data)
  ) {
    return data;
  }

  // queryKey() currently produces at most one key per type, so keeping only
  // the first datum per type is safe; a second query of the same type would
  // silently be dropped.
  const dataByType = new Map();
  data.forEach(datum => {
    const type = classifyData(datum);
    if (type && !dataByType.has(type)) dataByType.set(type, datum);
  });

  return queryKeys.map(key => dataByType.get(classifyQueryKey(key)));
};

widgetBootstrap({
  attributes,
  contract,
  Component,
  style: BetBuilderCss,
  loader,
  name: widgetName,
  themes,
  themeContract,
  queryKey,
  alignDehydratedData,
  version: '1.0.2' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
